/**
 * 智慧調整(REQ-20260523 Phase 1):
 *  - admin 用自然語言描述要改什麼
 *  - GPT 解析 → 預覽 plan
 *  - 確認後執行(後端會先拍 mysqldump backup)
 *  - 歷史列表 + 還原按鈕
 *
 * Phase 1 只 A 級(系統設定)可用,B/C 由後端攔下回 REJECT。
 */
import axios from 'axios';
import { useEffect, useRef, useState } from 'react';
import { Link, useBlocker } from 'react-router-dom';
import type {
  ApiSuccess,
  SmartAdjustClaudeLoginStart,
  SmartAdjustClaudeLoginSubmit,
  SmartAdjustClaudeStatus,
  SmartAdjustClaudeUpdate,
  SmartAdjustIntegrationsView,
  SmartAdjustLocalBlock,
  SmartAdjustLocalBlockStats,
  SmartAdjustLocalUsage,
  SmartAdjustOfficialUsage,
  SmartAdjustQuota,
  SmartAdjustSnapshot,
  SmartAdjustUsage,
} from '@app/shared';
import { Modal } from '../../components/Modal';
import { apiClient } from '../../lib/api-client';

function extractApiError(e: unknown, fallback: string): string {
  if (axios.isAxiosError(e)) {
    const data = e.response?.data as { error?: { message?: string } } | undefined;
    return data?.error?.message ?? e.message ?? fallback;
  }
  return e instanceof Error ? e.message : fallback;
}

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString('zh-TW');
}

/**
 * 6-step 進度條:對應後端 `phase` 欄位。
 * 順序:分析 → 時光紀錄 → 開發 → 驗證 → 更新 → 已完成。
 * status / phase 對應:
 *   - status=analyzing → phase=analyzing(第 1 步亮)
 *   - status=applying  → phase 為 backup/coding/verifying/deploying(對應第 2-5 步)
 *   - status=applied   → 全部勾,當作 done
 *   - status=failed    → 停在最後一個 active phase,加 ❌
 */
const PHASE_STEPS: Array<{ key: SmartAdjustSnapshot['phase']; label: string }> = [
  { key: 'analyzing', label: '分析' },
  { key: 'backup', label: '時光紀錄' },
  { key: 'coding', label: '開發' },
  { key: 'verifying', label: '驗證' },
  { key: 'deploying', label: '更新' },
  { key: 'done', label: '已完成' },
];

/** 附件上限,與後端 `SmartAdjustAttachmentSchema` 的 `.max(10)` 對齊 */
const MAX_ATTACHMENTS = 10;

/**
 * 輪詢途中可容忍的「連續拿不到回應」次數(逾時 / 斷線)。
 * 5 次 ≈ 最長 90 秒的容忍窗(每輪 3 秒間隔 + 單次最長 15 秒逾時),
 * 足以蓋過短暫網路抖動或 api 重啟;再多就沒必要讓使用者對著不會更新的畫面等。
 */
const POLL_MAX_CONSECUTIVE_FAILURES = 5;

/** 檔案挑選對話框的過濾條件,與後端 `SUPPORTED_ATTACHMENT_EXTS` 對齊 */
const ATTACHMENT_ACCEPT =
  'image/*,text/*,.txt,.md,.csv,.tsv,.json,.log,.yml,.yaml,.xml,.html,.sql';

/**
 * 分析尚未完成就要離開頁面時的確認文字。
 * 任務其實在後端 queue 內繼續跑,離開不會中斷 —— 但畫面上的輪詢會斷掉,
 * 所以要順便指路到「升級紀錄」,否則使用者會以為需求沒送出去。
 */
const ANALYZING_LEAVE_WARNING =
  'AI 分析尚未完成。\n\n' +
  '離開後分析仍會在背景繼續,可到「升級紀錄」查看結果,但這個畫面不會再自動更新。\n\n' +
  '確定要離開嗎?';

function fmtRelativeSec(iso: string | null | undefined): string {
  if (!iso) return '';
  const sec = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (sec < 60) return `${sec} 秒前`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} 分鐘前`;
  return `${Math.floor(min / 60)} 小時前`;
}

function ProgressStepper({ snap }: { snap: SmartAdjustSnapshot }): JSX.Element {
  const phase = snap.phase ?? null;
  const status = snap.status;
  // 算 active index
  let activeIdx = phase ? PHASE_STEPS.findIndex((s) => s.key === phase) : 0;
  if (status === 'applied' || status === 'rolledback') activeIdx = PHASE_STEPS.length - 1;
  if (activeIdx < 0) activeIdx = 0;
  const isFailed = status === 'failed';
  const isDone = status === 'applied' || (status === 'rolledback' && phase === 'done');
  return (
    <div className="bg-slate-50 border rounded p-3">
      <div className="flex items-center justify-between gap-2 overflow-x-auto">
        {PHASE_STEPS.map((s, i) => {
          const reached = i <= activeIdx;
          const active = i === activeIdx && !isDone;
          const failed = isFailed && i === activeIdx;
          return (
            <div key={s.key ?? i} className="flex-1 flex items-center gap-2 min-w-0">
              <div
                className={`shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold border-2 ${
                  failed
                    ? 'bg-red-100 border-red-400 text-red-700'
                    : isDone
                      ? 'bg-emerald-100 border-emerald-400 text-emerald-700'
                      : active
                        ? 'bg-violet-100 border-violet-400 text-violet-700 animate-pulse'
                        : reached
                          ? 'bg-emerald-50 border-emerald-300 text-emerald-600'
                          : 'bg-white border-slate-300 text-slate-400'
                }`}
              >
                {failed ? '✕' : reached && !active ? '✓' : i + 1}
              </div>
              <div
                className={`text-xs whitespace-nowrap ${
                  active
                    ? 'text-violet-700 font-semibold'
                    : reached
                      ? 'text-slate-700'
                      : 'text-slate-400'
                }`}
              >
                {s.label}
              </div>
              {i < PHASE_STEPS.length - 1 ? (
                <div
                  className={`flex-1 h-0.5 ${reached && i < activeIdx ? 'bg-emerald-300' : 'bg-slate-200'}`}
                />
              ) : null}
            </div>
          );
        })}
      </div>
      {snap.lastHeartbeatAt && !isDone && !isFailed ? (
        <div className="text-[11px] text-slate-500 mt-2">
          最近一次活動:{fmtRelativeSec(snap.lastHeartbeatAt)}
          {' · '}
          <span className="text-slate-400">超過 5 分鐘沒活動會自動標失敗</span>
        </div>
      ) : null}
    </div>
  );
}

/**
 * 升級項目列表用的精簡版 stepper:6 個小圓點 + 階段中文標 + 最近活動時戳。
 * Phase tracking 從 commit 4e20e90 起加,舊 snapshot 的 phase 可能是 null,
 * 那種情況 fallback 用 status 推算(planned → 第 1 步、applied → 第 6 步)。
 */
function CompactStepper({ snap }: { snap: SmartAdjustSnapshot }): JSX.Element {
  const status = snap.status;
  const phaseFromCol = snap.phase ?? null;
  // 推算 active step:phase 優先,phase 缺 → 由 status 推
  let activeIdx = phaseFromCol ? PHASE_STEPS.findIndex((s) => s.key === phaseFromCol) : -1;
  if (activeIdx < 0) {
    if (status === 'applied' || status === 'rolledback') activeIdx = PHASE_STEPS.length - 1;
    else if (status === 'planned') activeIdx = 0; // 分析完成,等使用者開始
    else if (status === 'analyzing') activeIdx = 0;
    else if (status === 'applying') activeIdx = 2; // 沒 phase 資訊就猜在 coding
    else activeIdx = 0;
  }
  const isInProgress = status === 'analyzing' || status === 'applying';
  const isFailed = status === 'failed';
  const isCancelled = status === 'cancelled';
  const isDone = status === 'applied' || status === 'rolledback';
  return (
    <div className="mt-1 flex items-center gap-1 flex-wrap text-[10px]">
      {PHASE_STEPS.map((s, i) => {
        const reached = i <= activeIdx;
        const active = i === activeIdx && isInProgress;
        const failedHere = isFailed && i === activeIdx;
        const cancelledHere = isCancelled && i === activeIdx;
        return (
          <span key={s.key ?? i} className="inline-flex items-center gap-0.5">
            <span
              className={`inline-block w-2 h-2 rounded-full ${
                failedHere
                  ? 'bg-red-500'
                  : cancelledHere
                    ? 'bg-slate-400'
                    : active
                      ? 'bg-violet-500 animate-pulse'
                      : reached
                        ? isDone
                          ? 'bg-emerald-500'
                          : isCancelled
                            ? 'bg-slate-300'
                            : 'bg-emerald-400'
                        : 'bg-slate-300'
              }`}
              title={s.label}
            />
            {/* 只在 active 步驟才寫文字標,避免列表太擠 */}
            {active ? (
              <span className="text-violet-700 font-medium">{s.label}</span>
            ) : failedHere ? (
              <span className="text-red-600">{s.label}</span>
            ) : cancelledHere ? (
              <span className="text-slate-500">{s.label}🚫</span>
            ) : null}
            {i < PHASE_STEPS.length - 1 ? (
              <span className={`w-2 h-px ${reached && i < activeIdx ? 'bg-emerald-300' : 'bg-slate-200'}`} />
            ) : null}
          </span>
        );
      })}
      {snap.lastHeartbeatAt ? (
        <span className="text-slate-400 ml-1">
          · 最後檢查:{fmtRelativeSec(snap.lastHeartbeatAt)}
        </span>
      ) : null}
    </div>
  );
}

/**
 * 三個 milestone 旗標(列表用迷你版):
 *   📦 備份(backupAt)/ 🚀 Push(pushedAt,僅 C 級)/ 📝 Redmine(redmineIssueId)
 * 不是進行中階段,是「已完成」事件 — 即使重新整理或刷新後也能一眼看到歷史 snapshot 達到哪些 milestone。
 */
function MilestoneBadges({
  snap,
  redmineBaseUrl,
}: {
  snap: SmartAdjustSnapshot;
  redmineBaseUrl: string;
}): JSX.Element | null {
  // analyzing / planned 還沒套用任何東西,milestone 都還沒發生,顯示出來只是雜訊;
  // applying 開始(備份完成)後才有實質意義,terminal 狀態(applied/failed/cancelled/rolledback)
  // 都保留顯示,讓 admin 看歷史。
  if (snap.status === 'analyzing' || snap.status === 'planned') return null;
  const isCTier = snap.tier === 'C';
  const redmineHref =
    snap.redmineIssueId && redmineBaseUrl
      ? `${redmineBaseUrl.replace(/\/$/, '')}/issues/${snap.redmineIssueId}`
      : null;
  return (
    <div className="mt-1 flex items-center gap-1.5 text-[10px] flex-wrap">
      <span
        className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded border ${
          snap.backupAt
            ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
            : 'bg-slate-50 text-slate-400 border-slate-200'
        }`}
        title={snap.backupAt ? `備份完成於 ${fmtDate(snap.backupAt)}` : '尚未備份'}
      >
        📦 備份{snap.backupAt ? ' ✓' : ' —'}
      </span>
      <span
        className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded border ${
          !isCTier
            ? 'bg-slate-50 text-slate-400 border-slate-200'
            : snap.pushedAt
              ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
              : 'bg-slate-50 text-slate-400 border-slate-200'
        }`}
        title={
          !isCTier
            ? '非 C 級不需 git push'
            : snap.pushedAt
              ? `git push 完成於 ${fmtDate(snap.pushedAt)}`
              : '尚未 push 到 git remote'
        }
      >
        🚀 Push{!isCTier ? ' N/A' : snap.pushedAt ? ' ✓' : ' —'}
      </span>
      {/* Redmine 工單號由系統開單時寫入,唯讀 —— 有 base URL 就做成連結 */}
      {redmineHref ? (
        <a
          href={redmineHref}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded border bg-emerald-50 text-emerald-700 border-emerald-200 hover:underline"
          title="開啟 Redmine 工單"
        >
          📝 #{snap.redmineIssueId}
        </a>
      ) : snap.redmineIssueId ? (
        <span
          className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded border bg-emerald-50 text-emerald-700 border-emerald-200"
          title="未設 Redmine 主機,僅顯示工單號"
        >
          📝 #{snap.redmineIssueId}
        </span>
      ) : (
        <span
          className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded border bg-slate-50 text-slate-400 border-slate-200"
          title="這次沒有開立 Redmine 工單(可能是整合未設定或開單失敗)"
        >
          📝 工單 —
        </span>
      )}
    </div>
  );
}

/**
 * 三個 milestone 旗標(詳情用完整版)— 顯示時戳 + Redmine 工單連結。
 * 主要用在「我獨自升級」主畫面 applied 狀態的展開區。
 */
function MilestoneDetail({
  snap,
  redmineBaseUrl,
}: {
  snap: SmartAdjustSnapshot;
  redmineBaseUrl: string;
}): JSX.Element {
  const isCTier = snap.tier === 'C';
  const redmineHref =
    snap.redmineIssueId && redmineBaseUrl
      ? `${redmineBaseUrl.replace(/\/$/, '')}/issues/${snap.redmineIssueId}`
      : null;

  return (
    <div className="mt-2 grid grid-cols-1 md:grid-cols-3 gap-2 text-xs">
      <div className="border rounded p-2 bg-white">
        <div className="text-[10px] text-slate-500 uppercase tracking-wide">📦 備份</div>
        <div className={`font-medium ${snap.backupAt ? 'text-emerald-700' : 'text-slate-400'}`}>
          {snap.backupAt ? '✓ 完成' : '—'}
        </div>
        {snap.backupAt ? (
          <div className="text-[10px] text-slate-500">{fmtDate(snap.backupAt)}</div>
        ) : null}
        {snap.dbBackupKey ? (
          <div
            className="text-[10px] font-mono text-slate-400 truncate"
            title={snap.dbBackupKey}
          >
            {snap.dbBackupKey.split('/').pop()}
          </div>
        ) : null}
      </div>
      <div className="border rounded p-2 bg-white">
        <div className="text-[10px] text-slate-500 uppercase tracking-wide">🚀 Git Push</div>
        <div
          className={`font-medium ${
            !isCTier ? 'text-slate-500' : snap.pushedAt ? 'text-emerald-700' : 'text-slate-400'
          }`}
        >
          {!isCTier ? '不適用(A/B 級)' : snap.pushedAt ? '✓ 完成' : '—'}
        </div>
        {snap.pushedAt ? (
          <div className="text-[10px] text-slate-500">{fmtDate(snap.pushedAt)}</div>
        ) : null}
        {snap.gitAfterSha ? (
          <div className="text-[10px] font-mono text-slate-400">
            {snap.gitAfterSha.slice(0, 8)}
          </div>
        ) : null}
      </div>
      <div className="border rounded p-2 bg-white">
        <div className="text-[10px] text-slate-500 uppercase tracking-wide">📝 Redmine 工單</div>
        {/* 工單號由系統開單時寫入,唯讀 */}
        {snap.redmineIssueId ? (
          <div className="font-medium text-emerald-700">
            {redmineHref ? (
              <a
                href={redmineHref}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:underline"
              >
                #{snap.redmineIssueId} ↗
              </a>
            ) : (
              <>#{snap.redmineIssueId}</>
            )}
          </div>
        ) : (
          <>
            <div className="text-slate-400 font-medium">—</div>
            <div className="text-[10px] text-slate-500 mt-0.5">未開立(整合未設定或開單失敗)</div>
          </>
        )}
      </div>
    </div>
  );
}

/** status 的中文顯示名。這一頁面向的是 admin,不該把 DB 的英文 enum 直接端上畫面 */
const STATUS_LABEL: Record<SmartAdjustSnapshot['status'], string> = {
  analyzing: '分析中',
  planned: '待確認',
  applying: '套用中',
  applied: '已套用',
  failed: '失敗',
  cancelled: '已取消',
  rolledback: '已還原',
};

function statusBadge(status: SmartAdjustSnapshot['status']): JSX.Element {
  const map: Record<SmartAdjustSnapshot['status'], string> = {
    analyzing: 'bg-violet-100 text-violet-700 animate-pulse',
    planned: 'bg-slate-100 text-slate-700',
    applying: 'bg-emerald-100 text-emerald-700 animate-pulse',
    applied: 'bg-emerald-100 text-emerald-700',
    failed: 'bg-red-100 text-red-700',
    cancelled: 'bg-slate-200 text-slate-600',
    rolledback: 'bg-amber-100 text-amber-700',
  };
  return (
    <span className={`text-[10px] px-1.5 py-0.5 rounded ${map[status]}`} title={status}>
      {STATUS_LABEL[status]}
    </span>
  );
}

function tierBadgeCls(tier: SmartAdjustSnapshot['tier']): string {
  const map: Record<SmartAdjustSnapshot['tier'], string> = {
    A: 'text-[10px] px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 font-medium',
    B: 'text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 font-medium',
    C: 'text-[10px] px-1.5 py-0.5 rounded bg-violet-100 text-violet-700 font-medium',
  };
  return map[tier];
}

export function SmartAdjustPage(): JSX.Element {
  const [request, setRequest] = useState('');
  const [expectedBehavior, setExpectedBehavior] = useState('');
  const [constraints, setConstraints] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [planning, setPlanning] = useState(false);
  const [planned, setPlanned] = useState<SmartAdjustSnapshot | null>(null);
  const [applying, setApplying] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [history, setHistory] = useState<SmartAdjustSnapshot[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  // 分頁標籤:需求輸入與歷史紀錄分開,避免列表變長後表單被推到很上面
  const [activeTab, setActiveTab] = useState<'new' | 'history' | 'usage'>('new');
  // 用量分頁(REQ-20260802002)。刻意 lazy load:多數時候使用者是來送需求的,
  // 沒必要每次進頁面都多打兩支 API。
  const [usage, setUsage] = useState<SmartAdjustUsage | null>(null);
  const [usageLoading, setUsageLoading] = useState(false);
  const [usageErr, setUsageErr] = useState<string | null>(null);
  // 額度狀態本身不再顯示(REQ-20260802007),但仍讀進來 —— 進度條的「距離重置」
  // 用官方 resetsAt 比本地推算準。這支 API 只讀 DB,不消耗額度。
  const [quota, setQuota] = useState<SmartAdjustQuota | null>(null);
  const [localUsage, setLocalUsage] = useState<SmartAdjustLocalUsage | null>(null);
  // 官方 /usage 面板(REQ-20260805001)。零額度成本,但每次要走一趟 SSH(實測 3 秒),
  // 所以做成按鈕按了才查,不跟著切分頁自動抓。
  const [official, setOfficial] = useState<SmartAdjustOfficialUsage | null>(null);
  const [officialLoading, setOfficialLoading] = useState(false);
  // 歷史列表翻頁。後端一次把該範圍全部給完(小清單)→ 依 ui-guidelines 在前端翻頁。
  const [historyPage, setHistoryPage] = useState(1);
  // 紀錄範圍:預設只看近 1 個月,按「全部紀錄」才整包撈(REQ-20260827001)
  const [historyScope, setHistoryScope] = useState<'recent' | 'all'>('recent');
  const [hintsOpen, setHintsOpen] = useState(false);
  // 頁首功能說明(A/B/C 分級那段)。預設收起,由標題列最右的 ? 按鈕切換。
  const [helpOpen, setHelpOpen] = useState(false);

  // 整合設定狀態(讀-only,僅顯示是否已設定;編輯在 /settings/smart-adjust-settings)
  const [intg, setIntg] = useState<SmartAdjustIntegrationsView | null>(null);

  // 主機端 Claude Code 的連線狀態(進頁面自動測一次,可點指示燈重測)
  const [claude, setClaude] = useState<SmartAdjustClaudeStatus | null>(null);
  const [claudeChecking, setClaudeChecking] = useState(false);
  // 環境檢查彈窗(REQ-20260826001)。點指示燈打開,裡面才有登入 / 更新這些動作。
  const [envOpen, setEnvOpen] = useState(false);

  const checkClaude = async (): Promise<void> => {
    setClaudeChecking(true);
    try {
      const res = await apiClient.get<ApiSuccess<SmartAdjustClaudeStatus>>(
        '/admin/smart-adjust/claude-status',
        { timeout: 30_000 },
      );
      setClaude(res.data.data);
    } catch (e) {
      // 探測本身失敗也是一種「沒連上」,照樣顯示紅燈而不是讓指示燈空白
      setClaude({
        ok: false,
        detail: extractApiError(e, '連線檢查失敗'),
        checkedAt: new Date().toISOString(),
      });
    } finally {
      setClaudeChecking(false);
    }
  };

  const loadIntegrations = async (): Promise<void> => {
    try {
      const res = await apiClient.get<ApiSuccess<SmartAdjustIntegrationsView>>(
        '/admin/smart-adjust/integrations',
      );
      setIntg(res.data.data);
    } catch (e) {
      console.warn('load integrations failed', e);
    }
  };

  /** 用量彙總 + 最近觀察到的額度狀態。兩者都是純讀,不消耗訂閱額度。 */
  const loadUsage = async (): Promise<void> => {
    setUsageLoading(true);
    setUsageErr(null);
    try {
      const [u, q, l] = await Promise.all([
        apiClient.get<ApiSuccess<SmartAdjustUsage>>('/admin/smart-adjust/usage'),
        apiClient.get<ApiSuccess<SmartAdjustQuota>>('/admin/smart-adjust/quota'),
        // 本地統計不消耗額度,可以跟著一起抓
        apiClient.get<ApiSuccess<SmartAdjustLocalUsage>>('/admin/smart-adjust/local-usage', {
          timeout: 90_000,
        }),
      ]);
      setUsage(u.data.data);
      setQuota(q.data.data);
      setLocalUsage(l.data.data);
    } catch (e) {
      setUsageErr(extractApiError(e, '讀取用量失敗'));
    } finally {
      setUsageLoading(false);
    }
  };

  /** 官方額度面板。查不到也照樣把原因塞進 state,讓畫面說得出「為什麼沒有」 */
  const loadOfficial = async (): Promise<void> => {
    setOfficialLoading(true);
    try {
      const res = await apiClient.get<ApiSuccess<SmartAdjustOfficialUsage>>(
        '/admin/smart-adjust/official-usage',
        { timeout: 60_000 },
      );
      setOfficial(res.data.data);
    } catch (e) {
      setOfficial({
        ok: false,
        error: extractApiError(e, '查詢失敗'),
        fetchedAt: null,
        text: '',
        parsed: null,
      });
    } finally {
      setOfficialLoading(false);
    }
  };

  const loadHistory = async (scope: 'recent' | 'all' = historyScope): Promise<void> => {
    setHistoryLoading(true);
    try {
      const res = await apiClient.get<ApiSuccess<SmartAdjustSnapshot[]>>(
        `/admin/smart-adjust/snapshots${scope === 'all' ? '?scope=all' : ''}`,
      );
      setHistory(res.data.data);
    } catch (e) {
      // 安靜失敗,不打擾主流程
      console.warn('load history failed', e);
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    void loadHistory();
    void loadIntegrations();
    void checkClaude();
  }, []);

  // 每次切到用量分頁都重抓。這份數字是呼叫當下才現算的,停留在頁面上不會自己更新 ——
  // 只在第一次載入的話,切走再切回來看到的是舊值,會誤以為額度區塊沒動。
  useEffect(() => {
    if (activeTab === 'usage' && !usageLoading) void loadUsage();
  }, [activeTab]);

  // 升級項目有任何 in-progress(analyzing/applying)→ 每 10 秒自動刷新 list,
  // 讓「最後檢查」時戳保持新鮮、phase 推進也能即時看到。
  // 沒有 in-progress 就不 poll,省 API quota。
  useEffect(() => {
    const hasInProgress = history.some(
      (s) => s.status === 'analyzing' || s.status === 'applying',
    );
    if (!hasInProgress) return;
    const t = setInterval(() => void loadHistory(), 10_000);
    return () => clearInterval(t);
  }, [history]);

  // 分析進行中離開頁面 → 先問一次(REQ-20260804002)。
  // 站內換頁(上方選單、「編輯整合設定」等)走 router blocker;
  // 關分頁 / 重新整理 / 直接打別的網址走 beforeunload,提示文字由瀏覽器決定不可自訂。
  const blocker = useBlocker(planning);
  useEffect(() => {
    if (blocker.state !== 'blocked') return;
    if (window.confirm(ANALYZING_LEAVE_WARNING)) blocker.proceed();
    else blocker.reset();
  }, [blocker]);

  useEffect(() => {
    if (!planning) return;
    const onBeforeUnload = (e: BeforeUnloadEvent): void => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [planning]);

  const addFiles = (incoming: File[]): void => {
    if (incoming.length === 0) return;
    setFiles((prev) => [...prev, ...incoming].slice(0, MAX_ATTACHMENTS));
  };

  const onPickFiles = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const list = e.target.files;
    if (!list) return;
    const arr: File[] = [];
    for (let i = 0; i < list.length; i += 1) {
      const f = list.item(i);
      if (f) arr.push(f);
    }
    addFiles(arr);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const onRemoveFile = (idx: number): void => {
    setFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  /**
   * Ctrl+V 貼上截圖。剪貼簿來的圖片 File 名稱多半是空的、或一律叫 `image.png`,
   * 同一批會互相覆蓋 → 重新命名成帶時戳的 ASCII 檔名(要一路經過 multipart、
   * Redmine 附件與主機檔案系統,不用非 ASCII 比較不會出事)。
   *
   * 不呼叫 preventDefault:剪貼簿同時有文字時,文字照樣貼進輸入框才符合直覺;
   * 純圖片的情況本來就沒有文字會被插入。
   */
  const onPasteAttachment = (e: React.ClipboardEvent): void => {
    const pasted: File[] = [];
    for (const item of Array.from(e.clipboardData?.items ?? [])) {
      if (item.kind !== 'file') continue;
      const f = item.getAsFile();
      if (!f) continue;
      // `image/svg+xml` → `svg`;拿不到就當 png
      const ext = (f.type.split('/')[1] ?? '').split('+')[0] || 'png';
      pasted.push(
        f.name && f.name !== 'image.png'
          ? f
          : new File([f], `pasted-${Date.now()}-${pasted.length + 1}.${ext}`, { type: f.type }),
      );
    }
    addFiles(pasted);
  };

  /**
   * 輪詢 snapshot 直到 status 離開 in-progress 狀態('analyzing' / 'applying')。
   * 兩個 in-progress 狀態:
   *   - analyzing:升級分析中(POST /plan 後)
   *   - applying:套用中(POST /apply 後,含 mysqldump 備份 + DB 寫入 / AI 改檔 + git push)
   * 看到 planned / applied / failed / rolledback 就停。
   * 超過 maxMs(預設 4 小時)主動放棄,前端跳逾時訊息(但後端任務在 queue 內會繼續跑)。
   * 單次 GET 逾時 / 斷線不算數 —— 連續 `POLL_MAX_CONSECUTIVE_FAILURES` 次才放棄。
   */
  const pollSnapshot = async (
    snapshotId: number,
    maxMs = 4 * 60 * 60_000, // 4 小時:後端 watchdog 5 分鐘 idle 會自動標 failed,
    // 前端不該硬切;留個 4 小時 cap 純粹避免分頁 leak
  ): Promise<SmartAdjustSnapshot> => {
    const t0 = Date.now();
    let consecutiveFailures = 0;
    while (Date.now() - t0 < maxMs) {
      await new Promise((r) => setTimeout(r, 3000));
      let s: SmartAdjustSnapshot;
      try {
        const r = await apiClient.get<ApiSuccess<SmartAdjustSnapshot>>(
          `/admin/smart-adjust/snapshots/${snapshotId}`,
          { timeout: 15_000 },
        );
        s = r.data.data;
      } catch (e) {
        // 沒有 response = 根本沒收到回應(逾時 / 斷線),任務多半還在 queue 內跑 → 再試幾輪。
        // 有 response(404 / 401 / 5xx…)是後端真的回了錯,照舊立刻上拋給呼叫端顯示。
        if (!axios.isAxiosError(e) || e.response) throw e;
        consecutiveFailures += 1;
        if (consecutiveFailures >= POLL_MAX_CONSECUTIVE_FAILURES) {
          throw new Error(
            `連續 ${POLL_MAX_CONSECUTIVE_FAILURES} 次取不到進度(網路不穩或連線中斷),` +
              '不過後端任務可能還在背景跑;請過一會兒到「升級紀錄」重整看看。',
          );
        }
        console.warn(
          `poll snapshot failed (${consecutiveFailures}/${POLL_MAX_CONSECUTIVE_FAILURES}), retrying`,
          e,
        );
        continue;
      }
      consecutiveFailures = 0;
      if (s.status !== 'analyzing' && s.status !== 'applying') return s;
    }
    throw new Error(
      '前端等候逾時(4 小時),不過後端任務在排隊中可能還在跑;請過一會兒到「升級紀錄」重整看看。',
    );
  };

  /**
   * 送出前的確認:後端會把 `failed` / `planned` 的舊任務一併標為已取消
   * (REQ-20260802003)。那是不可逆的 —— 尤其 `planned` 是已經花額度分析完、
   * 只差按「開始升級」的,收掉就得重跑。所以送出前要讓使用者看到會犧牲什麼。
   *
   * 進行中的(`analyzing` / `applying`)不會被取消,但它們會讓新任務排隊
   * (BullMQ concurrency=1)—— 這也該講,否則使用者會以為新需求卡住了。
   *
   * 沒有任何舊任務要處理時不打擾,直接回 true。
   */
  const confirmStaleWillBeCancelled = (): boolean => {
    const stale = history.filter((s) => s.status === 'planned' || s.status === 'failed');
    const running = history.filter((s) => s.status === 'analyzing' || s.status === 'applying');
    if (stale.length === 0 && running.length === 0) return true;

    const lines: string[] = [];
    if (stale.length > 0) {
      lines.push(`送出後,以下 ${stale.length} 筆未完成的任務會被自動取消:`);
      for (const s of stale.slice(0, 5)) {
        const label = s.status === 'planned' ? '待確認' : '失敗';
        lines.push(`  #${s.id}(${label}) ${s.request.slice(0, 30)}…`);
      }
      if (stale.length > 5) lines.push(`  …另有 ${stale.length - 5} 筆`);
      if (stale.some((s) => s.status === 'planned')) {
        lines.push('');
        lines.push('⚠️「待確認」是已經分析完、只差按「開始升級」的,取消後要重跑分析。');
      }
    }
    if (running.length > 0) {
      if (lines.length > 0) lines.push('');
      lines.push(
        `另有 ${running.length} 筆正在執行中,不會被取消 —— 新需求會排在它們後面。`,
      );
    }
    lines.push('');
    lines.push('確定要送出嗎?');
    return window.confirm(lines.join('\n'));
  };

  const onPlan = async (): Promise<void> => {
    if (!request.trim()) return;
    if (!confirmStaleWillBeCancelled()) return;
    setPlanning(true);
    setErr(null);
    setPlanned(null);
    try {
      // POST 後端立刻回 snapshot(status=analyzing),不會等 AI 分析 / 改檔
      // 有附件走 multipart;沒有走 JSON(快、不需 parse)
      let res;
      if (files.length > 0) {
        const form = new FormData();
        form.append('request', request.trim());
        if (expectedBehavior.trim()) form.append('expectedBehavior', expectedBehavior.trim());
        if (constraints.trim()) form.append('constraints', constraints.trim());
        for (const f of files) form.append('attachment', f, f.name);
        res = await apiClient.post<ApiSuccess<SmartAdjustSnapshot>>(
          '/admin/smart-adjust/plan',
          form,
          {
            // 附件要先進 MinIO、主機工作區、Redmine,比純文字久
            headers: { 'Content-Type': 'multipart/form-data' },
            timeout: 120_000,
          },
        );
      } else {
        const body: Record<string, unknown> = { request: request.trim() };
        if (expectedBehavior.trim()) body.expectedBehavior = expectedBehavior.trim();
        if (constraints.trim()) body.constraints = constraints.trim();
        res = await apiClient.post<ApiSuccess<SmartAdjustSnapshot>>(
          '/admin/smart-adjust/plan',
          body,
          { timeout: 60_000 },
        );
      }
      // analyzing 立刻顯示,history 也顯示一筆 analyzing,再開始輪詢
      setPlanned(res.data.data);
      void loadHistory();
      const finalSnap = await pollSnapshot(res.data.data.id);
      setPlanned(finalSnap);
      void loadHistory();
    } catch (e) {
      setErr(extractApiError(e, '預覽失敗'));
    } finally {
      setPlanning(false);
    }
  };

  const onApply = async (): Promise<void> => {
    if (!planned) return;
    const hardWarning =
      planned.restoreComplexity === 'hard'
        ? '\n\n⚠️ 此升級還原難度【困難】:' +
          (planned.involvesSchemaChange
            ? '會改動資料庫結構。還原時程式可回舊版,但資料庫新增的欄位/表會留下,要徹底清乾淨需工程介入。'
            : '結構性變動,還原可能需要工程協助。')
        : '';
    if (
      !window.confirm(
        '開始升級?\n系統會先做時光機備份(資料 + 程式版本),完成後才開始實際升級。' +
          '\n若升級後不滿意,可在「升級項目」按還原回到備份狀態。' +
          hardWarning,
      )
    )
      return;
    setApplying(true);
    setErr(null);
    try {
      // POST 立刻回 snapshot(status=applying),實際備份 + 套用 + git push 背景排隊跑
      const res = await apiClient.post<ApiSuccess<SmartAdjustSnapshot>>(
        `/admin/smart-adjust/apply/${planned.id}`,
        null,
        { timeout: 60_000 },
      );
      setPlanned(res.data.data);
      void loadHistory();
      // 一直 poll 到狀態離開 applying
      const finalSnap = await pollSnapshot(res.data.data.id);
      setPlanned(finalSnap);
      void loadHistory();
    } catch (e) {
      setErr(extractApiError(e, '套用失敗'));
      void loadHistory();
    } finally {
      setApplying(false);
    }
  };

  const onRestore = async (id: number): Promise<void> => {
    if (
      !window.confirm(
        '還原這筆升級?系統會把對應的設定 / 資料 / 程式碼還原到升級前的狀態。' +
          '\n注意:此筆升級之後若還有其他變動,還原時可能受影響。',
      )
    )
      return;
    setErr(null);
    try {
      await apiClient.post<ApiSuccess<SmartAdjustSnapshot>>(
        `/admin/smart-adjust/restore/${id}`,
      );
      void loadHistory();
    } catch (e) {
      setErr(extractApiError(e, '還原失敗'));
    }
  };

  const onCancel = async (id: number): Promise<void> => {
    const snap = history.find((h) => h.id === id);
    const isInProgress = snap?.status === 'analyzing' || snap?.status === 'applying';
    const msg = isInProgress
      ? '中斷這個進行中的任務?\n排隊中 → 直接從佇列移除;已開始執行 → 副作用(備份 / commit)可能已發生,只能停止後續更新狀態。'
      : '取消這個任務?\n它已經停住了,這個動作只是把 status 標成 cancelled 並關掉對應的 Redmine 工單。紀錄會保留。';
    if (!window.confirm(msg)) return;
    setErr(null);
    try {
      await apiClient.post<ApiSuccess<SmartAdjustSnapshot>>(
        `/admin/smart-adjust/cancel/${id}`,
      );
      void loadHistory();
    } catch (e) {
      setErr(extractApiError(e, '中斷失敗'));
    }
  };

  const onReset = (): void => {
    setPlanned(null);
    setRequest('');
    setExpectedBehavior('');
    setConstraints('');
    setFiles([]);
    setErr(null);
  };

  const planObj = planned?.plan as { tier: string; actions?: unknown[]; reason?: string } | null;
  const isReject = planObj?.tier === 'REJECT';
  const isAnalyzing = planned?.status === 'analyzing';
  const isApplying = planned?.status === 'applying';

  const HISTORY_PAGE_SIZE = 10;
  const historyTotalPages = Math.max(1, Math.ceil(history.length / HISTORY_PAGE_SIZE));
  // 總頁數可能變少(例如切到別的篩選 / 重新載入),若當前頁超出範圍就收回最後一頁
  const historyPageSafe = Math.min(historyPage, historyTotalPages);
  const pagedHistory = history.slice(
    (historyPageSafe - 1) * HISTORY_PAGE_SIZE,
    historyPageSafe * HISTORY_PAGE_SIZE,
  );
  // 只有「最新一筆已套用」能還原。還原是把整個系統退回那一刻,不是單獨 undo 這一筆;
  // 跳著還原會連後面的升級一起丟掉。要退更多步就從最新的逐筆往回還原。
  // (後端 restore() 也擋一次,這裡只是不要把做不到的按鈕擺出來)
  const newestAppliedId = history.reduce<number | null>(
    (acc, s) => (s.status === 'applied' && (acc === null || s.id > acc) ? s.id : acc),
    null,
  );

  // 版面寬度不自己設 max-w:頂層頁面一律由 App shell 的 max-w-6xl 決定
  // (對齊 InvoicesPage 等);自己再加限制會比其他頁窄一截。
  return (
    <section className="grid gap-4">
      <h2 className="text-lg font-semibold flex items-center gap-2">
        <span className="inline-flex items-center justify-center text-[11px] font-bold rounded-md bg-violet-100 text-violet-700 px-1.5 py-0.5">
          AI
        </span>
        我獨自升級
        <span className="text-xs text-slate-500 font-normal">(A+B+C 全面實作)</span>
        <ClaudeStatusBadge
          status={claude}
          checking={claudeChecking}
          onOpen={() => setEnvOpen(true)}
        />
        {/* 說明預設收起 — 老手每次進來都要跳過同一段文字很浪費版面 */}
        <button
          type="button"
          onClick={() => setHelpOpen((o) => !o)}
          aria-expanded={helpOpen}
          aria-label={helpOpen ? '收起功能說明' : '展開功能說明'}
          title="功能說明"
          className={`shrink-0 w-6 h-6 rounded-full border text-xs font-bold leading-none ${
            helpOpen
              ? 'bg-violet-100 border-violet-300 text-violet-700'
              : 'border-slate-300 text-slate-500 hover:bg-slate-100'
          }`}
        >
          ?
        </button>
      </h2>
      <ClaudeEnvDialog
        open={envOpen}
        onClose={() => setEnvOpen(false)}
        status={claude}
        checking={claudeChecking}
        onRecheck={() => void checkClaude()}
      />
      {helpOpen ? (
        <div className="text-xs text-slate-600 leading-relaxed grid gap-1 bg-slate-50 border rounded p-3">
          <p>
            用自然語言描述要做的調整,AI 解析成升級計畫並標明還原難度,你看過 OK 才按開始升級。
            每次升級前自動拍時光機備份(資料 + 程式版本),異常 / 想還原可從下方升級項目一鍵 rollback。
            <strong>AI 不會主動拒絕架構性需求</strong> — 能轉成計畫就轉,還原代價由你看 restoreComplexity 自己決定。
          </p>
          <ul className="pl-4 list-disc">
            <li><span className="font-medium text-blue-700">A 級</span> 系統設定 — 還原難度 容易</li>
            <li><span className="font-medium text-amber-700">B 級</span> 業務資料批改 — 還原難度 容易~中等</li>
            <li><span className="font-medium text-violet-700">C 級</span> 程式碼 / 新功能 / 排程 / schema 變動 — 純 code 中等;含 schema 變動 困難(資料庫欄位/表還原需工程介入)</li>
          </ul>
        </div>
      ) : null}

      {/* 整合設定狀態(僅顯示;編輯在 /settings/smart-adjust-settings)*/}
      <div className="bg-white border rounded px-4 py-2 flex items-center gap-3 text-sm">
        <span className="text-xs text-slate-500">整合設定:</span>
        <span className="text-xs">
          Redmine{' '}
          {intg?.redmine.url ? (
            <span className="text-emerald-700">✓</span>
          ) : (
            <span className="text-slate-400">未設</span>
          )}
        </span>
        <span className="text-xs text-slate-300">·</span>
        <span className="text-xs">
          GitLab{' '}
          {intg?.gitlab.url ? (
            <span className="text-emerald-700">✓</span>
          ) : (
            <span className="text-slate-400">未設</span>
          )}
        </span>
        <Link
          to="/settings/smart-adjust-settings"
          className="ml-auto text-xs text-blue-600 hover:underline"
        >
          編輯整合設定 →
        </Link>
      </div>

      {/* 分頁標籤:需求輸入 / 歷史紀錄。樣式沿用 CustomerConsolePage 的 TabButton */}
      <div className="flex gap-1 border-b" data-testid="smart-adjust-tabs">
        <SmartAdjustTab
          active={activeTab === 'new'}
          onClick={() => setActiveTab('new')}
          testId="tab-new"
        >
          新增升級
        </SmartAdjustTab>
        <SmartAdjustTab
          active={activeTab === 'history'}
          onClick={() => setActiveTab('history')}
          testId="tab-history"
        >
          升級紀錄{history.length > 0 ? ` (${history.length})` : ''}
        </SmartAdjustTab>
        <SmartAdjustTab
          active={activeTab === 'usage'}
          onClick={() => setActiveTab('usage')}
          testId="tab-usage"
        >
          用量
        </SmartAdjustTab>
      </div>

      {activeTab === 'new' ? (
        <>

      <div className="bg-white border rounded p-4 grid gap-3">
        <div className="grid gap-2">
          <label className="text-sm">
            <span className="text-slate-700 block mb-1">要做什麼? *</span>
            <textarea
              value={request}
              onChange={(e) => setRequest(e.target.value)}
              onPaste={onPasteAttachment}
              disabled={planning || applying}
              rows={4}
              placeholder="例:把 SMTP 的寄件人名稱改成「訪談系統客服」(截圖可直接 Ctrl+V 貼進來)"
              className="w-full border rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
          </label>
          <AttachmentPicker
            files={files}
            disabled={planning || applying}
            inputRef={fileInputRef}
            onPick={onPickFiles}
            onRemove={onRemoveFile}
            onPaste={onPasteAttachment}
          />
        </div>

        {/* 進階提示 — 摺起來預設不擋畫面 */}
        <button
          type="button"
          onClick={() => setHintsOpen((o) => !o)}
          className="text-xs text-slate-600 hover:text-slate-900 self-start"
        >
          {hintsOpen ? '▾' : '▸'} 更多 AI 提示(預期行為 / 限制條件,都可選填)
        </button>
        {hintsOpen ? (
          <div className="grid gap-2 bg-slate-50 border rounded p-3">
            <label className="grid gap-1 text-sm">
              <span className="text-xs text-slate-600">預期行為(完成後應該是什麼樣)</span>
              <textarea
                value={expectedBehavior}
                onChange={(e) => setExpectedBehavior(e.target.value)}
                disabled={planning || applying}
                rows={2}
                placeholder="例:寄出測試信時,顯示名稱會是「訪談系統客服」"
                className="w-full border rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
            </label>
            <label className="grid gap-1 text-sm">
              <span className="text-xs text-slate-600">限制條件(不要動到什麼 / 一定要保留什麼)</span>
              <textarea
                value={constraints}
                onChange={(e) => setConstraints(e.target.value)}
                disabled={planning || applying}
                rows={2}
                placeholder="例:不要動到 SMTP host / port,只改 fromName"
                className="w-full border rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
            </label>
          </div>
        ) : null}

        <div className="flex gap-2 items-center">
          <button
            type="button"
            onClick={() => void onPlan()}
            disabled={!request.trim() || planning || applying}
            className="bg-blue-600 text-white px-3 py-1.5 rounded hover:bg-blue-700 disabled:opacity-50"
          >
            {planning
              ? '分析中…(AI 會實際讀程式碼,較大的需求可能要數分鐘)'
              : '🔍 升級分析'}
          </button>
          {planned ? (
            <button
              type="button"
              onClick={onReset}
              className="border rounded px-3 py-1.5 text-slate-700 hover:bg-slate-100"
            >
              清除
            </button>
          ) : null}
        </div>
      </div>

      {err ? (
        <div role="alert" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3">
          {err}
        </div>
      ) : null}

      {planned ? (
        <div className="bg-white border rounded p-4 grid gap-3">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-semibold text-slate-800">
              {isAnalyzing
                ? 'AI 分析中…'
                : isApplying
                  ? '升級套用中…'
                  : isReject
                    ? 'AI 分析結果'
                    : planned.status === 'planned'
                      ? '升級分析(等待您確認)'
                      : '升級結果'}
            </h3>
            {statusBadge(planned.status)}
            <span className="text-xs text-slate-500 ml-auto">
              項目 #{planned.id}
              {!isAnalyzing && !isApplying
                ? ` · AI 用量 in/out: ${planned.tokensIn ?? 0}/${planned.tokensOut ?? 0}`
                : ''}
            </span>
          </div>
          {isAnalyzing ? (
            <div className="grid gap-2">
              <ProgressStepper snap={planned} />
              <div className="bg-violet-50 border border-violet-200 rounded p-3 text-sm text-violet-800">
                <div className="flex items-center gap-2 font-medium">
                  <span className="inline-block w-2 h-2 rounded-full bg-violet-500 animate-pulse" />
                  AI 正在分析這個請求,並在背景核對現有程式碼。
                </div>
                <div className="text-xs text-violet-700 mt-1">
                  此頁每 3 秒自動更新一次,完成後會自動顯示結果。沒有時間限制,
                  只要 AI 持續有活動就會等下去;連續 5 分鐘沒活動才視為失敗。
                  你也可以離開頁面,稍後到「升級項目」查看。
                </div>
              </div>
            </div>
          ) : isApplying ? (
            <div className="grid gap-2">
              <ProgressStepper snap={planned} />
              <div className="bg-emerald-50 border border-emerald-200 rounded p-3 text-sm text-emerald-800">
                <div className="flex items-center gap-2 font-medium">
                  <span className="inline-block w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                  升級套用中…
                </div>
                <div className="text-xs text-emerald-700 mt-1">
                  按進度條看目前所在階段(時光紀錄 → 開發 → 驗證 → 更新 → 完成)。
                  沒有時間限制,只要持續有活動就會等下去;5 分鐘沒活動才視為失敗。
                  你也可以離開頁面,稍後到「升級項目」查看。
                </div>
              </div>
            </div>
          ) : isReject ? (
            <div className="bg-red-50 border border-red-200 rounded p-3 text-sm text-red-700">
              <div className="font-semibold mb-1">❌ AI 判斷此請求無法執行</div>
              <div className="text-xs whitespace-pre-wrap">{planObj?.reason}</div>
            </div>
          ) : (
            <>
              {/* 白話說明 — 醒目大字 */}
              {planned.userExplanation ? (
                <div className="bg-emerald-50 border-l-4 border-emerald-400 rounded-r p-3 text-sm text-slate-800 leading-relaxed whitespace-pre-wrap">
                  {planned.userExplanation}
                </div>
              ) : (
                <div className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">
                  {planned.summary}
                </div>
              )}

              {/* 還原難度 + schema 警告 */}
              {planned.restoreComplexity ? (
                <div
                  className={`rounded p-2 text-xs flex items-start gap-2 ${
                    planned.restoreComplexity === 'hard'
                      ? 'bg-red-50 border border-red-200 text-red-800'
                      : planned.restoreComplexity === 'medium'
                        ? 'bg-amber-50 border border-amber-200 text-amber-800'
                        : 'bg-slate-50 border border-slate-200 text-slate-700'
                  }`}
                >
                  <span className="font-semibold whitespace-nowrap">
                    還原難度:
                    {planned.restoreComplexity === 'easy'
                      ? '🟢 容易'
                      : planned.restoreComplexity === 'medium'
                        ? '🟡 中等'
                        : '🔴 困難'}
                  </span>
                  <span className="leading-relaxed">
                    {planned.restoreComplexity === 'easy'
                      ? '按還原 30 秒內精準回到變更前狀態'
                      : planned.restoreComplexity === 'medium'
                        ? '按還原會 5-10 分鐘,系統會自動重新部署回舊版'
                        : planned.involvesSchemaChange
                          ? '此升級會改動資料庫結構。還原時程式可以回舊版,但資料庫新增的欄位 / 表會保留,要徹底清乾淨需工程介入。'
                          : '此升級結構性較大,還原可能需要工程協助'}
                  </span>
                </div>
              ) : null}

              <div className="text-xs text-slate-500">
                <span className={tierBadgeCls(planned.tier)}>tier {planned.tier}</span>
                <span className="ml-2">技術摘要:{planned.summary}</span>
              </div>

              {/* B 級:dry-run 預覽(每個 op 受影響筆數 + sample)*/}
              {planned.tier === 'B' && planned.bDryRun ? (
                <div className="border rounded">
                  <div className="bg-amber-50 px-2 py-1.5 text-xs text-amber-800 font-medium border-b">
                    Dry-run 預覽(尚未執行,僅 SELECT 估算)
                  </div>
                  <div className="grid gap-2 p-2">
                    {planned.bDryRun.perOp.map((op, i) => (
                      <div key={i} className="text-xs">
                        <div className="font-medium text-slate-700">
                          {op.describe}{' '}
                          <span className="text-slate-400">
                            ({op.table},受影響 {op.affectedCount} 筆)
                          </span>
                        </div>
                        {op.sample.length > 0 ? (
                          <details className="text-[11px] text-slate-500">
                            <summary className="cursor-pointer">前 {op.sample.length} 筆 sample</summary>
                            <pre className="mt-1 px-2 py-1 bg-slate-50 border rounded overflow-x-auto">
{JSON.stringify(op.sample, null, 2)}
                            </pre>
                          </details>
                        ) : null}
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}

              {/* C 級:分析時 dry-run 拿到的 diff,或 apply 後 commit 的 diff */}
              {planned.tier === 'C' && planned.cDiff ? (
                <details className="text-xs border rounded">
                  <summary className="cursor-pointer bg-violet-50 px-2 py-1.5 text-violet-800 font-medium border-b">
                    程式碼改動細節(工程用,一般使用者可略過)
                  </summary>
                  <pre className="px-2 py-1 text-[11px] overflow-x-auto max-h-96 bg-slate-50">
{planned.cDiff}
                  </pre>
                </details>
              ) : null}

              <details className="text-xs text-slate-500 bg-slate-50 border rounded">
                <summary className="cursor-pointer px-2 py-1.5 font-medium">
                  技術細節(工程用,一般使用者可略過)
                </summary>
                <pre className="px-2 pb-2 overflow-x-auto text-[11px]">
{JSON.stringify(planned.plan, null, 2)}
                </pre>
              </details>

              {planned.status === 'planned' ? (
                <div className="flex gap-2 items-center pt-2 border-t flex-wrap">
                  <button
                    type="button"
                    onClick={() => void onApply()}
                    disabled={applying}
                    className="bg-emerald-600 text-white px-4 py-2 rounded font-medium hover:bg-emerald-700 disabled:opacity-50"
                  >
                    {applying
                      ? '升級中…(備份 + 執行,C 級可能 1-5 分鐘)'
                      : '🚀 開始升級'}
                  </button>
                  <span className="text-xs text-slate-500">
                    開始升級前系統會自動拍時光機備份;若升級後不滿意,可在下方「升級項目」一鍵還原
                  </span>
                </div>
              ) : planned.status === 'applied' ? (
                <div className="space-y-2">
                  <div className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded p-2">
                    ✓ 升級完成 — 系統已自動在 Redmine 建立追蹤工單並回寫結果
                  </div>
                  <MilestoneDetail snap={planned} redmineBaseUrl={intg?.redmine.url ?? ''} />
                </div>
              ) : (
                <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2">
                  升級狀態:{STATUS_LABEL[planned.status]}
                  {planned.errorMessage ? ` — ${planned.errorMessage}` : ''}
                </div>
              )}
            </>
          )}
        </div>
      ) : null}
        </>
      ) : null}

      {activeTab === 'history' ? (
      <div className="bg-white border rounded p-4">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold text-slate-800">
            升級項目(時光回朔機)
            <span className="ml-2 text-xs font-normal text-slate-500">
              {historyScope === 'recent' ? '近 1 個月' : '全部紀錄'}
            </span>
          </h3>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => {
                const next = historyScope === 'recent' ? 'all' : 'recent';
                setHistoryScope(next);
                setHistoryPage(1);
                void loadHistory(next);
              }}
              disabled={historyLoading}
              className="text-xs border rounded px-2 py-1 text-slate-700 hover:bg-slate-100 disabled:opacity-50"
            >
              {historyScope === 'recent' ? '全部紀錄' : '近 1 個月'}
            </button>
            <button
              type="button"
              onClick={() => void loadHistory()}
              disabled={historyLoading}
              className="text-xs border rounded px-2 py-1 text-slate-700 hover:bg-slate-100 disabled:opacity-50"
            >
              {historyLoading ? '更新中…' : '重新整理'}
            </button>
          </div>
        </div>
        {history.length === 0 ? (
          <div className="text-sm text-slate-500 p-3">
            {historyScope === 'recent'
              ? '近 1 個月沒有升級紀錄。按「全部紀錄」看更早以前的。'
              : '尚無紀錄'}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-xs text-slate-600">
                <th className="px-2 py-1.5 w-10">#</th>
                <th className="px-2 py-1.5 w-40">時間</th>
                <th className="px-2 py-1.5">原始需求 / AI 結果</th>
                <th className="px-2 py-1.5 w-20">狀態</th>
                <th className="px-2 py-1.5 w-28 text-right whitespace-nowrap">操作</th>
              </tr>
            </thead>
            <tbody>
              {pagedHistory.map((s) => (
                <tr key={s.id} className="border-b last:border-b-0 hover:bg-slate-50 align-top">
                  <td className="px-2 py-1.5 text-xs text-slate-500">{s.id}</td>
                  <td className="px-2 py-1.5 text-xs">
                    {fmtDate(s.createdAt)}
                    <div className="text-[10px] text-slate-400">
                      {s.createdByName ?? '—'}
                    </div>
                  </td>
                  <td className="px-2 py-1.5 text-xs">
                    {/* 原始需求 — 使用者當時打的字,最重要,放最上面 */}
                    <div
                      className="text-slate-900 font-medium border-l-2 border-slate-300 pl-2 whitespace-pre-wrap break-words"
                      title={s.request}
                    >
                      {s.request.length > 200 ? `${s.request.slice(0, 200)}…` : s.request}
                    </div>
                    {/* AI 解析後的結果 / 拒絕原因 — userExplanation 優先,沒有就用 summary */}
                    <div className="text-slate-600 mt-1">
                      {s.userExplanation ?? s.summary}
                    </div>
                    <div className="text-[10px] text-slate-400 mt-0.5 flex items-center gap-1 flex-wrap">
                      <span className={tierBadgeCls(s.tier)}>{s.tier}</span>
                      {s.restoreComplexity === 'hard' ? (
                        <span className="bg-red-100 text-red-700 px-1 rounded">還原困難</span>
                      ) : s.restoreComplexity === 'medium' ? (
                        <span className="bg-amber-100 text-amber-700 px-1 rounded">還原中等</span>
                      ) : s.restoreComplexity === 'easy' ? (
                        <span className="bg-slate-100 text-slate-600 px-1 rounded">還原容易</span>
                      ) : null}
                      {s.userExplanation ? (
                        <span className="text-slate-400">· {s.summary.slice(0, 60)}</span>
                      ) : null}
                    </div>
                    {/* 6 階段精簡進度條 + 最後檢查時戳 */}
                    <CompactStepper snap={s} />
                    <MilestoneBadges snap={s} redmineBaseUrl={intg?.redmine.url ?? ''} />
                  </td>
                  <td className="px-2 py-1.5">{statusBadge(s.status)}</td>
                  <td className="px-2 py-1.5 text-right whitespace-nowrap">
                    {s.status === 'applied' ? (
                      s.id === newestAppliedId ? (
                        <button
                          type="button"
                          onClick={() => void onRestore(s.id)}
                          className="text-amber-700 hover:underline text-xs whitespace-nowrap"
                        >
                          還原
                        </button>
                      ) : (
                        <span
                          className="text-slate-400 text-xs whitespace-nowrap"
                          title={`只能還原最新一筆已套用的升級(#${newestAppliedId})。還原會讓整個系統回到該次升級之前,跳著還原會連後面的升級一起丟掉。`}
                        >
                          —
                        </span>
                      )
                    ) : s.status === 'analyzing' || s.status === 'applying' ? (
                      <button
                        type="button"
                        onClick={() => void onCancel(s.id)}
                        className="text-red-700 hover:underline text-xs whitespace-nowrap"
                      >
                        中斷
                      </button>
                    ) : s.status === 'planned' || s.status === 'failed' ? (
                      // 已經停住的任務叫「中斷」是錯的 —— 沒有東西可以中斷。
                      // 它做的是「標成已取消 + 關掉 Redmine 工單」,所以叫「取消」。
                      // 「中斷」保留給上面真的會去清 queue job 的那兩種狀態。
                      <button
                        type="button"
                        onClick={() => void onCancel(s.id)}
                        className="text-slate-600 hover:text-red-700 hover:underline text-xs whitespace-nowrap"
                        title="標記為已取消並關掉 Redmine 工單,紀錄保留"
                      >
                        取消
                      </button>
                    ) : (
                      // cancelled / rolledback = 終態。REQ-20260802003 起不提供刪除 ——
                      // 紀錄留著才追得回「上次那個需求為什麼沒成功、花了多少」
                      <span className="text-slate-300 text-xs whitespace-nowrap">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {history.length > HISTORY_PAGE_SIZE ? (
          <div className="flex justify-between items-center mt-4 text-sm text-slate-600">
            <span>
              {historyScope === 'recent' ? '近 1 個月共 ' : '全部共 '}
              {history.length} 筆
            </span>
            <div className="flex gap-2">
              <button
                type="button"
                disabled={historyPageSafe <= 1}
                onClick={() => setHistoryPage(historyPageSafe - 1)}
                className="border px-3 py-1 rounded disabled:opacity-50"
              >
                上一頁
              </button>
              <span>
                {historyPageSafe} / {historyTotalPages}
              </span>
              <button
                type="button"
                disabled={historyPageSafe >= historyTotalPages}
                onClick={() => setHistoryPage(historyPageSafe + 1)}
                className="border px-3 py-1 rounded disabled:opacity-50"
              >
                下一頁
              </button>
            </div>
          </div>
        ) : null}
      </div>
      ) : null}

      {activeTab === 'usage' ? (
        <UsagePanel
          usage={usage}
          quota={quota}
          localUsage={localUsage}
          official={official}
          officialLoading={officialLoading}
          onLoadOfficial={() => void loadOfficial()}
          loading={usageLoading}
          error={usageErr}
          onReload={() => void loadUsage()}
        />
      ) : null}
    </section>
  );
}

/** token 數字用 k / M 收斂 —— 用量動輒六七位數,原樣列出反而看不出量級 */
function fmtTokens(n: number): string {
  if (n === 0) return '—';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return String(n);
}

function fmtCost(n: number): string {
  return n > 0 ? `$${n.toFixed(2)}` : '—';
}

/**
 * 用量進度條。`pct` 已算好(0-100),超過 100 會夾在 100 但文字照實顯示 ——
 * 分母是推估值,超過只代表「這一輪比以往都兇」,不是壞掉。
 */
function UsageBar({
  label,
  pct,
  right,
  tone = 'blue',
}: {
  label: string;
  pct: number;
  right: string;
  tone?: 'blue' | 'amber' | 'emerald';
}): JSX.Element {
  const clamped = Math.max(0, Math.min(100, pct));
  const bar =
    tone === 'amber' ? 'bg-amber-500' : tone === 'emerald' ? 'bg-emerald-500' : 'bg-blue-500';
  return (
    <div className="grid grid-cols-[6rem_1fr_auto] items-center gap-2 text-xs">
      <span className="text-slate-600">{label}</span>
      <div className="h-2 rounded-full bg-slate-200 overflow-hidden">
        <div className={`h-full ${bar}`} style={{ width: `${clamped}%` }} />
      </div>
      <span className="text-slate-600 whitespace-nowrap font-mono text-[11px]">
        {pct.toFixed(1)}% · {right}
      </span>
    </div>
  );
}

/** 分鐘 → 「X 小時 Y 分」 */
function fmtMinutes(m: number): string {
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return h > 0 ? `${h} 小時 ${mm} 分` : `${mm} 分`;
}

/**
 * 當前 5 小時 session 區塊的進度(REQ-20260802006,形式參考
 * Claude-Code-Usage-Monitor)。
 *
 * ⚠️ 進度條的**分母是推估值**:Anthropic 沒有公開額度換算方式,所以拿使用者
 * 自己歷史區塊的 P90 當參考線 —— 這是參考工具採用的做法。樣本太少時參考價值
 * 有限,所以把「已完成區塊數」一併顯示,讓人自己判斷要不要信。
 *
 * 「距離重置」優先用官方 `resetsAt`(來自 rate_limit_event),沒有才退回本地
 * 推算的區塊結束時間 —— 實測兩者只差 10 分鐘。
 */
function BlockProgress({
  block,
  current,
  resetsAt,
}: {
  block: SmartAdjustLocalBlockStats | null;
  current: SmartAdjustLocalBlock | null;
  resetsAt: number | null;
}): JSX.Element {
  if (!block || !current) {
    return (
      <div className="text-sm text-slate-500 border rounded p-3 bg-slate-50">
        目前這個五小時區塊還沒有任何 Claude Code 活動。
      </div>
    );
  }
  const tokenPct = block.p90Tokens > 0 ? (current.totalTokens / block.p90Tokens) * 100 : 0;
  const msgPct = block.p90Messages > 0 ? (current.messages / block.p90Messages) * 100 : 0;
  const elapsed = current.elapsedMinutes ?? 0;
  // 官方 resetsAt 較準,但它是「最近一次觀察到的」—— 若那個時間已經過去,
  // 代表額度視窗早就重置過而還沒有新的觀察,這時用它會顯示「還有 0 分」。
  // 過期就退回本地推算的區塊結束時間(實測兩者只差 10 分鐘)。
  const officialRemain =
    resetsAt !== null ? Math.round((resetsAt * 1000 - Date.now()) / 60000) : -1;
  const remainMin = officialRemain > 0 ? officialRemain : (current.remainingMinutes ?? 0);
  const timePct = ((300 - remainMin) / 300) * 100;

  const modelTotal = Object.values(current.byModel).reduce((a, b) => a + b, 0);
  const models = Object.entries(current.byModel)
    .sort((a, b) => b[1] - a[1])
    .map(([m, v]) => `${m.replace(/^claude-/, '')} ${((v / (modelTotal || 1)) * 100).toFixed(1)}%`);

  return (
    <div className="border rounded p-3 grid gap-2 bg-slate-50">
      <div className="flex items-baseline justify-between gap-2">
        <h4 className="text-xs font-semibold text-slate-800">
          當前 5 小時額度區塊
          <span className="ml-1 font-normal text-slate-500">
            {fmtDate(current.start)} 起 · 已過 {fmtMinutes(elapsed)}
          </span>
        </h4>
        <span className="text-[11px] text-slate-400">
          參考線來自 {block.completedCount} 個歷史區塊
        </span>
      </div>
      <UsageBar
        label="Token 用量"
        pct={tokenPct}
        right={`${fmtTokens(current.totalTokens)} / ${fmtTokens(block.p90Tokens)}`}
      />
      <UsageBar
        label="訊息數"
        pct={msgPct}
        right={`${current.messages} / ${block.p90Messages}`}
        tone="emerald"
      />
      <UsageBar
        label="距離重置"
        pct={timePct}
        right={`還有 ${fmtMinutes(remainMin)}`}
        tone="amber"
      />
      <div className="grid grid-cols-[6rem_1fr] gap-2 text-xs">
        <span className="text-slate-600">模型分布</span>
        <span className="text-slate-700">{models.join(' · ') || '—'}</span>
      </div>
      <div className="grid grid-cols-[6rem_1fr] gap-2 text-xs">
        <span className="text-slate-600">燃燒速率</span>
        <span className="text-slate-700 font-mono text-[11px]">
          {block.burnTokensPerMin.toLocaleString('zh-TW')} tokens/分(近 60 分鐘)
        </span>
      </div>
      <p className="text-[11px] text-slate-400">
        分母是
        <strong className="text-slate-500">依你自己的歷史區塊推估的參考線(P90)</strong>
        ,歷史最高為 {fmtTokens(block.maxTokens)} / {block.maxMessages} 則。
        <strong className="text-slate-500">不是 Anthropic 官方上限</strong> ——
        官方從未公開額度換算方式,超過 100% 只代表這一輪比以往都兇,不代表一定會被擋。
      </p>
    </div>
  );
}

/**
 * 官方 `/usage` 面板與本地估算的對帳(REQ-20260805001)。
 *
 * 為什麼要有這一塊:上面的進度條分母是**我們自己用歷史 P90 推的參考線**,
 * 跟官方的百分比不是同一把尺,使用者拿去跟 `/usage` 對會發現對不上。
 * 官方數字現在拿得到了(`claude -p "/usage"`,零額度成本),就把兩邊並排,
 * 差多少直接看得見,不必再猜。
 *
 * 兩邊**讀的是同一批資料** —— 官方面板自己就標明「Approximate, based on local
 * sessions on this machine」。差異來自換算公式(官方未公開),不是資料來源不同。
 *
 * 原文完整照登:官方隨時可能改格式或多一行(例如 Opus / Fable 的週額度),
 * 解析壞掉時原文仍然是對的。
 */
function OfficialUsage({
  official,
  localUsage,
}: {
  official: SmartAdjustOfficialUsage;
  localUsage: SmartAdjustLocalUsage | null;
}): JSX.Element {
  if (!official.ok) {
    return (
      <div role="alert" className="text-sm text-slate-600 border rounded p-3 bg-slate-50">
        查不到官方數字:{official.error}
      </div>
    );
  }
  const block = localUsage?.ok ? localUsage.block : null;
  const cur = block?.current ?? null;
  const localBlockPct =
    block && cur && block.p90Tokens > 0 ? (cur.totalTokens / block.p90Tokens) * 100 : null;
  // 官方的「本週」對應我們的近 7 天。我們沒有週的百分比(沒有官方分母),
  // 只能給絕對量 —— 這正是兩邊互補的地方。
  const week = localUsage?.ok ? localUsage.windows.last7d : undefined;
  const weekTokens = week ? week.totalInputTokens + week.outputTokens : null;

  return (
    <div className="border rounded p-3 grid gap-2 bg-white">
      <div className="flex items-baseline justify-between gap-2">
        <h4 className="text-xs font-semibold text-slate-800">
          官方 /usage 面板
          <span className="ml-2 text-[11px] font-normal text-emerald-700">不消耗額度</span>
        </h4>
        {official.fetchedAt ? (
          <span className="text-[11px] text-slate-400">查詢於 {fmtDate(official.fetchedAt)}</span>
        ) : null}
      </div>

      {official.parsed ? (
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b text-left text-[11px] text-slate-600">
              <th className="px-2 py-1 w-28">區間</th>
              <th className="px-2 py-1 w-24">官方</th>
              <th className="px-2 py-1">本地估算</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-b">
              <td className="px-2 py-1 text-slate-700">當前 5 小時</td>
              <td className="px-2 py-1 font-mono">
                {official.parsed.sessionPct !== null ? `${official.parsed.sessionPct}%` : '—'}
              </td>
              <td className="px-2 py-1 font-mono text-slate-600">
                {localBlockPct !== null && cur
                  ? `${localBlockPct.toFixed(1)}% · ${fmtTokens(cur.totalTokens)}`
                  : '—'}
              </td>
            </tr>
            <tr>
              <td className="px-2 py-1 text-slate-700">本週 / 近 7 天</td>
              <td className="px-2 py-1 font-mono">
                {official.parsed.weekPct !== null ? `${official.parsed.weekPct}%` : '—'}
              </td>
              <td className="px-2 py-1 font-mono text-slate-600">
                {weekTokens !== null ? `${fmtTokens(weekTokens)}(無官方分母,不換算 %)` : '—'}
              </td>
            </tr>
          </tbody>
        </table>
      ) : null}

      <pre className="text-[11px] bg-slate-50 border rounded p-2 overflow-x-auto whitespace-pre-wrap text-slate-700">
{official.text}
      </pre>

      <p className="text-[11px] text-slate-400">
        官方面板自己標明是
        <strong className="text-slate-500">「based on local sessions on this machine」</strong>
        —— 跟上面的本地估算讀的是<strong className="text-slate-500">同一批 session 記錄</strong>,
        差異來自換算公式(官方未公開),不是有一邊算錯。統計對象是
        <strong className="text-slate-500">主機上的 Claude Code 帳號</strong>,
        不是你自己電腦上的額度。
      </p>
    </div>
  );
}

/**
 * 用量面板:當前額度區塊的進度條 + 本系統每日 token / 成本彙總。
 *
 * REQ-20260802007 移除了「訂閱額度」狀態區與跨區間累計表 —— 使用者反映
 * `five_hour` / 原始 JSON / 五段區間那些東西看不懂,留著只是雜訊。
 * 進度條已經回答了「這一輪用掉多少、還有多久重置」,那才是要看的。
 * (後端 API 都還在:`/quota`、`/quota/check`、`local-usage` 的 windows /
 * byModel 仍照常回傳,只是沒有畫面入口。)
 *
 * ⚠️ **成本不是帳單金額** —— 訂閱制是固定月費,`total_cost_usd` 是「同樣用量
 * 換算成 API 價格」的估值,只能當「這次任務有多重」的相對指標。
 *
 * 表格樣式沿用本頁「升級項目」那張的密度(px-2 py-1.5),不用 ui-guidelines 的
 * px-4 py-2 —— 同一頁兩張表用不同密度看起來更像 bug。
 */
function UsagePanel({
  usage,
  quota,
  localUsage,
  official,
  officialLoading,
  onLoadOfficial,
  loading,
  error,
  onReload,
}: {
  usage: SmartAdjustUsage | null;
  /** 只為了拿官方 `resetsAt` 讓「距離重置」準一點;額度狀態本身不再顯示 */
  quota: SmartAdjustQuota | null;
  localUsage: SmartAdjustLocalUsage | null;
  /** 官方 /usage 面板;null = 還沒查過(REQ-20260805001)*/
  official: SmartAdjustOfficialUsage | null;
  officialLoading: boolean;
  onLoadOfficial: () => void;
  loading: boolean;
  error: string | null;
  onReload: () => void;
}): JSX.Element {
  return (
    <div className="bg-white border rounded p-4 grid gap-4">
      {/* ─── 當前額度區塊的用量進度(本地 transcript 加總)─── */}
      <div className="grid gap-2">
        <div className="flex items-center justify-between gap-2">
          <h3 className="text-sm font-semibold text-slate-800">
            Claude Code 用量
            <span className="ml-2 text-[11px] font-normal text-emerald-700">不消耗額度</span>
          </h3>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onReload}
              disabled={loading}
              className="text-xs border rounded px-2 py-1 text-slate-700 hover:bg-slate-100 disabled:opacity-50"
            >
              {loading ? '更新中…' : '重新整理'}
            </button>
            <button
              type="button"
              onClick={onLoadOfficial}
              disabled={officialLoading}
              className="text-xs border rounded px-2 py-1 text-blue-700 border-blue-200 hover:bg-blue-50 disabled:opacity-50"
              title="跑一次 claude /usage 取官方數字比對(不消耗額度)"
            >
              {officialLoading ? '查詢中…' : official ? '重查官方' : '與官方對帳'}
            </button>
          </div>
        </div>
        {localUsage === null ? (
          <div role="status" className="text-sm text-slate-500">
            載入中…
          </div>
        ) : !localUsage.ok ? (
          <div role="alert" className="text-sm text-slate-500 border rounded p-3 bg-slate-50">
            讀不到本地統計:{localUsage.error}
          </div>
        ) : (
          <BlockProgress
            block={localUsage.block}
            current={localUsage.block?.current ?? null}
            resetsAt={quota?.info?.resetsAt ?? null}
          />
        )}
        {official ? <OfficialUsage official={official} localUsage={localUsage} /> : null}
      </div>

      {/* ─── 用量彙總 ─── */}
      <div className="grid gap-2 pt-2 border-t">
        <h3 className="text-sm font-semibold text-slate-800">
          用量統計(近 {usage?.days ?? 30} 天)
        </h3>

        {loading && usage === null ? (
          <div role="status" className="text-sm text-slate-500 p-3">
            載入中…
          </div>
        ) : error ? (
          <div role="alert" className="text-sm text-red-600 p-3">
            載入失敗:{error}
          </div>
        ) : usage === null || usage.daily.length === 0 ? (
          <div className="text-sm text-slate-500 p-3">
            這段期間沒有升級任務。到「新增升級」送出需求後,用量會自動記錄在這裡。
          </div>
        ) : (
          <>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-xs text-slate-600">
                  <th className="px-2 py-1.5 w-28">日期</th>
                  <th className="px-2 py-1.5 w-14 text-right">任務</th>
                  <th className="px-2 py-1.5 text-right">分析 in / out</th>
                  <th className="px-2 py-1.5 text-right">改 code in / out</th>
                  <th className="px-2 py-1.5 w-20 text-right">估算成本</th>
                </tr>
              </thead>
              <tbody>
                {usage.daily.map((d) => (
                  <tr key={d.date} className="border-b last:border-b-0 hover:bg-slate-50">
                    <td className="px-2 py-1.5 whitespace-nowrap">{d.date}</td>
                    <td className="px-2 py-1.5 text-right text-slate-600">{d.tasks}</td>
                    <td className="px-2 py-1.5 text-right font-mono text-xs">
                      {fmtTokens(d.analyzeTokensIn)} / {fmtTokens(d.analyzeTokensOut)}
                    </td>
                    <td className="px-2 py-1.5 text-right font-mono text-xs">
                      {fmtTokens(d.codeTokensIn)} / {fmtTokens(d.codeTokensOut)}
                    </td>
                    <td className="px-2 py-1.5 text-right font-mono text-xs">
                      {fmtCost(d.costUsd)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 font-medium">
                  <td className="px-2 py-1.5">合計</td>
                  <td className="px-2 py-1.5 text-right">{usage.total.tasks}</td>
                  <td className="px-2 py-1.5 text-right font-mono text-xs">
                    {fmtTokens(usage.total.analyzeTokensIn)} /{' '}
                    {fmtTokens(usage.total.analyzeTokensOut)}
                  </td>
                  <td className="px-2 py-1.5 text-right font-mono text-xs">
                    {fmtTokens(usage.total.codeTokensIn)} / {fmtTokens(usage.total.codeTokensOut)}
                  </td>
                  <td className="px-2 py-1.5 text-right font-mono text-xs">
                    {fmtCost(usage.total.costUsd)}
                  </td>
                </tr>
              </tfoot>
            </table>
            {usage.missingCost > 0 || usage.missingUsage > 0 ? (
              <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5 grid gap-0.5">
                {usage.missingCost > 0 ? (
                  <p>
                    {usage.missingCost === usage.total.tasks
                      ? '這段期間的任務全部'
                      : `其中 ${usage.missingCost} 筆`}
                    沒有「改 code」與「成本」記錄 —— 那兩欄自 2026-08-02 起才開始記錄,
                    更早的任務一律顯示「—」,
                    <strong className="text-amber-800">不代表成本為 0</strong>。
                  </p>
                ) : null}
                {usage.missingUsage > 0 ? (
                  <p>
                    另有 {usage.missingUsage} 筆連分析用量都沒有(多半是分析階段就失敗、
                    還沒拿到回報就中斷)。
                  </p>
                ) : null}
              </div>
            ) : null}
            <p className="text-[11px] text-slate-400">
              「in」是模型實際讀入的 token,<strong className="text-slate-500">含快取讀取</strong>;
              快取的計價遠低於一般輸入,別拿 token 數自己乘單價 —— 要看花費請用右邊那欄。
              成本本身是 Claude Code 回報的估值(「同樣用量換算成 API 價格」),
              <strong className="text-slate-500">不是帳單金額</strong>,訂閱制是固定月費,
              只適合拿來比較任務之間的輕重。
            </p>
          </>
        )}
      </div>
    </div>
  );
}

/**
 * 主機端 Claude Code 的環境指示燈(標題列,? 按鈕左側)。
 *
 * 為什麼要有:改 code 與需求分析都靠主機上的 Claude Code。mac mini 開了 FileVault,
 * 重開機後沒人登入 ep 帳號時服務不會自己恢復 —— 這種情況下設定全都在,
 * 但送出升級一定失敗。與其讓使用者打完需求才踩到,不如進頁面就先講。
 *
 * ⚠️ REQ-20260826001:舊版只驗「SSH 通 + claude 執行檔在」,**刻意不碰憑證** ——
 * 結果 2026-08-26 訂閱憑證過期那次全程綠燈,使用者要等到跑完分析才看到
 * 「OAuth session expired」。現在 ok 也把登入狀態算進去,未登入一律紅燈。
 *
 * 點一下開環境檢查彈窗(重新檢查 / 登入 / 更新都在裡面)。狀態不會自動輪詢:
 * 這是每次都會真的 SSH 出去的探測,不該背景空轉。
 */
function ClaudeStatusBadge({
  status,
  checking,
  onOpen,
}: {
  status: SmartAdjustClaudeStatus | null;
  checking: boolean;
  onOpen: () => void;
}): JSX.Element {
  const loggedOut = status?.env ? !status.env.auth.loggedIn : false;
  const cls = checking
    ? 'bg-slate-100 text-slate-500 border-slate-200'
    : status?.ok
      ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
      : 'bg-red-50 text-red-700 border-red-200';
  const dot = checking
    ? 'bg-slate-400 animate-pulse'
    : status?.ok
      ? 'bg-emerald-500'
      : 'bg-red-500';
  const label = checking
    ? '檢查中…'
    : status?.ok
      ? 'Claude Code 已就緒'
      : loggedOut
        ? 'Claude 未登入'
        : 'Claude Code 未連線';
  const title = checking
    ? '正在檢查主機上 Claude Code 的環境'
    : status
      ? `${status.detail}\n(檢查於 ${fmtDate(status.checkedAt)};點擊開啟環境檢查)`
      : '點擊開啟環境檢查';
  return (
    <button
      type="button"
      onClick={onOpen}
      title={title}
      aria-live="polite"
      className={`ml-auto shrink-0 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-[11px] font-normal ${cls}`}
    >
      <span className={`inline-block w-1.5 h-1.5 rounded-full ${dot}`} />
      {label}
    </button>
  );
}

/** 環境檢查彈窗裡的一列(項目名 + 綠勾/紅叉 + 右側值)。 */
function EnvRow({
  ok,
  name,
  value,
  note,
}: {
  ok: boolean;
  name: string;
  value: string;
  note?: string | undefined;
}): JSX.Element {
  return (
    <div className="flex items-center gap-2 border rounded-lg px-3 py-2.5 bg-white">
      <span className={ok ? 'text-emerald-600' : 'text-red-600'}>{ok ? '✓' : '✗'}</span>
      <span className="font-medium text-sm">{name}</span>
      <span className="ml-auto text-xs text-slate-500 text-right">
        {value}
        {note ? <span className="block text-[11px] text-slate-400">{note}</span> : null}
      </span>
    </div>
  );
}

/**
 * 主機端環境檢查彈窗(REQ-20260826001)。
 *
 * 四個項目對應「送出升級真的會用到」的前置條件:SSH 連得到主機、Node 在、
 * Claude CLI 在、**而且登入還有效**。最後一項是這個彈窗存在的主因 ——
 * 憑證過期是唯一一個「全部設定都對、但一定失敗」的狀態,而且只會愈來愈常見
 * (refresh token 有期限)。
 *
 * 登入流程刻意做成兩段:先拿授權網址,使用者在**自己的**瀏覽器授權
 * (redirect 走 platform.claude.com,不是 localhost,所以不必人在 mac mini 前面),
 * 再把網頁給的 code 貼回來。主機端會留一個等輸入的 session,關掉彈窗會取消它。
 */
function ClaudeEnvDialog({
  open,
  onClose,
  status,
  checking,
  onRecheck,
}: {
  open: boolean;
  onClose: () => void;
  status: SmartAdjustClaudeStatus | null;
  checking: boolean;
  onRecheck: () => void;
}): JSX.Element {
  const env = status?.env ?? null;
  const sshOk = !!env;

  const [loginUrl, setLoginUrl] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState<'' | 'login' | 'submit' | 'update'>('');
  const [msg, setMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

  // 關掉彈窗就把主機端等待中的登入 session 收掉 —— 留著它會佔住 claude 行程,
  // 讓「更新版本」誤判成有任務在跑。
  const close = (): void => {
    if (loginUrl) {
      void apiClient.post('/admin/smart-adjust/claude-login/cancel').catch(() => undefined);
    }
    setLoginUrl(null);
    setCode('');
    setMsg(null);
    onClose();
  };

  const startLogin = async (): Promise<void> => {
    setBusy('login');
    setMsg(null);
    try {
      const res = await apiClient.post<ApiSuccess<SmartAdjustClaudeLoginStart>>(
        '/admin/smart-adjust/claude-login/start',
        {},
        { timeout: 60_000 },
      );
      const data = res.data.data;
      if (data.ok && data.url) {
        setLoginUrl(data.url);
        // 直接開新分頁,少一次點擊;被瀏覽器擋掉的話下面還是有連結可以點
        window.open(data.url, '_blank', 'noopener');
      } else {
        setMsg({ kind: 'err', text: data.error ?? '啟動登入失敗' });
      }
    } catch (e) {
      setMsg({ kind: 'err', text: extractApiError(e, '啟動登入失敗') });
    } finally {
      setBusy('');
    }
  };

  const submitCode = async (): Promise<void> => {
    setBusy('submit');
    setMsg(null);
    try {
      const res = await apiClient.post<ApiSuccess<SmartAdjustClaudeLoginSubmit>>(
        '/admin/smart-adjust/claude-login/submit',
        { code: code.trim() },
        { timeout: 90_000 },
      );
      const data = res.data.data;
      if (data.ok && data.loggedIn) {
        setLoginUrl(null);
        setCode('');
        setMsg({ kind: 'ok', text: '登入成功' });
        onRecheck();
      } else {
        setMsg({ kind: 'err', text: data.error ?? '授權碼未能完成登入' });
      }
    } catch (e) {
      setMsg({ kind: 'err', text: extractApiError(e, '送出授權碼失敗') });
    } finally {
      setBusy('');
    }
  };

  const runUpdate = async (): Promise<void> => {
    setBusy('update');
    setMsg(null);
    try {
      const res = await apiClient.post<ApiSuccess<SmartAdjustClaudeUpdate>>(
        '/admin/smart-adjust/claude-update',
        {},
        { timeout: 360_000 },
      );
      const data = res.data.data;
      if (data.ok) {
        setMsg({ kind: 'ok', text: `已更新到 ${data.version ?? '最新版'}` });
        onRecheck();
      } else {
        setMsg({ kind: 'err', text: data.error ?? '更新失敗' });
      }
    } catch (e) {
      setMsg({ kind: 'err', text: extractApiError(e, '更新失敗') });
    } finally {
      setBusy('');
    }
  };

  return (
    <Modal open={open} onClose={close} title="Claude 環境檢查" widthClass="max-w-lg">
      <div className="grid gap-3">
        <p className="text-xs text-slate-500">
          檢查 mac mini 上「我獨自升級」所需的環境
          {env?.arch ? `(架構:${env.arch})` : ''}。這些檢查不會消耗訂閱額度。
        </p>

        <EnvRow
          ok={sshOk}
          name="SSH 連線"
          value={sshOk ? '已連線' : '連不到主機'}
          note={sshOk ? undefined : status?.detail}
        />
        <EnvRow
          ok={!!env?.node.ok}
          name="Node.js"
          value={env?.node.version ?? '—'}
          note={env?.node.ok === false ? env.node.detail : undefined}
        />
        <EnvRow
          ok={!!env?.cli.ok}
          name="Claude CLI"
          value={env?.cli.version ?? '—'}
          note={env?.cli.updateAvailable ? `可更新 → ${env.cli.latest}` : undefined}
        />
        <EnvRow
          ok={!!env?.auth.loggedIn}
          name="Claude 登入"
          value={env?.auth.loggedIn ? '已登入' : '未登入'}
          note={env && !env.auth.loggedIn ? env.auth.detail : undefined}
        />

        {/* ── 未登入:兩段式授權 ───────────────────────────── */}
        {env && !env.auth.loggedIn ? (
          <div className="border rounded-lg p-3 bg-amber-50 border-amber-200 grid gap-2">
            <div className="text-sm font-medium text-amber-900">Claude 尚未登入,升級功能無法運作</div>
            {!loginUrl ? (
              <button
                type="button"
                onClick={() => void startLogin()}
                disabled={busy !== ''}
                className="justify-self-start px-3 py-1.5 rounded bg-amber-500 text-white text-sm font-medium disabled:opacity-50"
              >
                {busy === 'login' ? '啟動中…' : '登入 Claude'}
              </button>
            ) : (
              <div className="grid gap-2">
                <div className="text-xs text-amber-900">
                  1. 在瀏覽器開啟授權頁並按同意(哪台電腦都可以):
                </div>
                <a
                  href={loginUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-blue-700 underline break-all"
                >
                  {loginUrl}
                </a>
                <div className="text-xs text-amber-900">2. 把授權頁給的代碼貼在這裡:</div>
                <div className="flex gap-2">
                  <input
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    placeholder="例:abc123#xyz789"
                    className="flex-1 border rounded px-2 py-1 text-sm font-mono"
                  />
                  <button
                    type="button"
                    onClick={() => void submitCode()}
                    disabled={busy !== '' || !code.trim()}
                    className="px-3 py-1.5 rounded bg-emerald-600 text-white text-sm font-medium disabled:opacity-50"
                  >
                    {busy === 'submit' ? '登入中…' : '完成登入'}
                  </button>
                </div>
                <div className="text-[11px] text-amber-700">
                  授權連結 10 分鐘內有效,逾時請重新按登入。
                </div>
              </div>
            )}
          </div>
        ) : null}

        {/* ── 有新版可更新 ─────────────────────────────────── */}
        {env?.cli.updateAvailable ? (
          <div className="border rounded-lg p-3 bg-slate-50 grid gap-2">
            <div className="text-sm font-medium">
              Claude 有新版本可更新({env.cli.version} → {env.cli.latest})
            </div>
            <button
              type="button"
              onClick={() => void runUpdate()}
              disabled={busy !== '' || env.busy}
              title={env.busy ? '主機上有 Claude 任務進行中,更新會把它腳下的執行檔換掉' : undefined}
              className="justify-self-start px-3 py-1.5 rounded bg-amber-400 text-slate-900 text-sm font-medium disabled:opacity-50"
            >
              {busy === 'update' ? '更新中…(約 1 分鐘)' : '更新 Claude(npm -g,約 1 分鐘)'}
            </button>
            {env.busy ? (
              <div className="text-[11px] text-red-600">
                主機上有 Claude 任務進行中,請等任務結束後再更新。
              </div>
            ) : null}
            <code className="text-[11px] bg-white border rounded px-2 py-1 text-slate-600">
              npm install -g @anthropic-ai/claude-code
            </code>
          </div>
        ) : null}

        {msg ? (
          <div
            className={`text-sm rounded px-3 py-2 border ${
              msg.kind === 'ok'
                ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
                : 'bg-red-50 border-red-200 text-red-700'
            }`}
          >
            {msg.text}
          </div>
        ) : null}

        {status?.ok ? (
          <div className="text-sm rounded px-3 py-2 border bg-emerald-50 border-emerald-200 text-emerald-800">
            環境正常 ✓ 可以開始升級了{env?.cli.updateAvailable ? '(Claude 有新版可更新)' : ''}。
          </div>
        ) : null}

        <div className="flex justify-end pt-1">
          <button
            type="button"
            onClick={onRecheck}
            disabled={checking || busy !== ''}
            className="px-3 py-1.5 rounded border text-sm hover:bg-slate-50 disabled:opacity-50"
          >
            {checking ? '檢查中…' : '↻ 重新檢查'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

/**
 * 附件挑選區:檔案選取 + Ctrl+V 貼上截圖 + 已選清單。
 *
 * 只收圖片與文字檔 —— 分析執行體是主機上的 Claude Code,它用 Read 工具打開這些檔案;
 * pdf / docx / zip 這類二進位文件它讀不出內容,收下只會讓使用者以為 AI 看到了。
 * 後端 `isSupportedAttachment()` 會再擋一次。
 */
function AttachmentPicker({
  files,
  disabled,
  inputRef,
  onPick,
  onRemove,
  onPaste,
}: {
  files: File[];
  disabled: boolean;
  inputRef: React.RefObject<HTMLInputElement>;
  onPick: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onRemove: (idx: number) => void;
  onPaste: (e: React.ClipboardEvent) => void;
}): JSX.Element {
  const full = files.length >= MAX_ATTACHMENTS;
  return (
    <div
      className="grid gap-1 text-sm border border-dashed rounded p-2 bg-slate-50"
      onPaste={onPaste}
    >
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs text-slate-600">
          附件(可選,最多 {MAX_ATTACHMENTS} 個)
        </span>
        <input
          ref={inputRef}
          type="file"
          accept={ATTACHMENT_ACCEPT}
          multiple
          onChange={onPick}
          disabled={disabled || full}
          className="text-xs"
        />
      </div>
      {files.length > 0 ? (
        <div className="flex flex-wrap gap-2 mt-1">
          {files.map((f, i) => (
            <div
              key={`${f.name}-${i}`}
              className="text-[11px] bg-white border rounded px-2 py-1 flex items-center gap-2"
            >
              <span className="font-mono">{f.name}</span>
              <span className="text-slate-400">({(f.size / 1024).toFixed(0)} KB)</span>
              <button
                type="button"
                onClick={() => onRemove(i)}
                disabled={disabled}
                className="text-red-600 hover:text-red-800 disabled:opacity-50"
                aria-label={`移除 ${f.name}`}
              >
                ✕
              </button>
            </div>
          ))}
        </div>
      ) : null}
      <span className="text-[11px] text-slate-400">
        截圖可直接 <kbd className="border rounded px-1">Ctrl</kbd>+
        <kbd className="border rounded px-1">V</kbd> 貼上。
        支援圖片(png/jpg/gif/webp/svg)與文字檔(txt/md/csv/json/log/yml…);
        AI 分析時會實際打開來看。附件同時會掛到本次的 Redmine 工單上。
        {full ? <span className="text-amber-600"> 已達上限。</span> : null}
      </span>
    </div>
  );
}

/**
 * 分頁標籤按鈕。樣式與 CustomerConsolePage 的 TabButton 一致 ——
 * 兩處都是「同一區塊內切換不同資料視圖」,不另創一套視覺。
 */
function SmartAdjustTab(props: {
  active: boolean;
  onClick: () => void;
  testId: string;
  children: React.ReactNode;
}): JSX.Element {
  const base = 'px-4 py-2 text-sm border-b-2 -mb-px transition-colors';
  const cls = props.active
    ? 'border-blue-600 text-blue-700 font-semibold'
    : 'border-transparent text-slate-600 hover:text-slate-900';
  return (
    <button
      type="button"
      onClick={props.onClick}
      className={`${base} ${cls}`}
      data-testid={props.testId}
    >
      {props.children}
    </button>
  );
}
